"use client";

import type { useChat } from "@ai-sdk/react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { toProjectIdFromIndexWorkerId } from "../shared/index-worker-id";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { DELETE_INDEX_WORKER_NAME } from "./constants";
import type { DeleteIndexWorkerInput } from "./schema";

const DeleteIndexWorkerConfirmQuery = graphql(`
  query DeleteIndexWorkerConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on IndexWorker {
        id
        name
        note
      }
    }
  }
`);

const ProjectDataQuery = graphql(`
  query IndexWorkerDeleteConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on Project {
        name
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

/**
 * Index Worker の削除確認 UI を表示する。
 * ツール入力が揃ったタイミングで詳細をフェッチして一覧化する。
 */
export function DeleteIndexWorkerConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const router = useRouter();
  const isTarget =
    isToolUIPart(part) && getToolName(part) === DELETE_INDEX_WORKER_NAME;
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<DeleteIndexWorkerInput>;
  const indexWorkerId = input.indexWorkerId ?? "";

  const [{ data, fetching }] = useQuery({
    query: DeleteIndexWorkerConfirmQuery,
    variables: { id: indexWorkerId },
    pause: !(isTarget && isInputAvailable && indexWorkerId),
  });

  const projectId = indexWorkerId
    ? toProjectIdFromIndexWorkerId(indexWorkerId)
    : null;
  const [{ data: projectData, fetching: projectFetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId ?? "" },
    pause: !(isTarget && isInputAvailable && projectId),
  });

  if (!isTarget || !isInputAvailable) return null;

  const workerNode =
    data?.node?.__typename === "IndexWorker" ? data.node : null;
  const projectNode =
    projectData?.node &&
    (projectData.node.__typename === "IndexWorkerProject" ||
      projectData.node.__typename === "SearchAnalyticsProject" ||
      projectData.node.__typename === "UserAnalyticsProject")
      ? projectData.node
      : null;

  const indexWorkerName = workerNode?.name ?? i18n._(msg`Unknown`);

  return (
    <div className="p-4 text-sm space-y-4">
      <div>
        <div className="mb-2 font-medium">
          <Trans>Delete Index Worker</Trans>
        </div>
        <div className="mb-2">
          <Trans>
            Are you sure you want to delete <strong>{indexWorkerName}</strong>?
          </Trans>
        </div>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <Trans>Name</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <span className="font-medium">{indexWorkerName}</span>
            )}
          </li>
          {workerNode?.note ? (
            <li>
              <Trans>Note</Trans>: {workerNode.note}
            </li>
          ) : null}
          {projectId ? (
            <li>
              <Trans>Project</Trans>:{" "}
              {projectFetching ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                <span className="font-medium">
                  {projectNode?.name ?? <Trans>Unknown project</Trans>}
                </span>
              )}
            </li>
          ) : null}
        </ul>
      </div>
      <Alert variant="destructive" className="text-xs">
        <AlertTriangleIcon className="h-4 w-4" />
        <AlertTitle>
          <Trans>This action is irreversible</Trans>
        </AlertTitle>
        <AlertDescription>
          <Trans>
            The configuration and schedule for this Index Worker will be
            permanently removed.
          </Trans>
        </AlertDescription>
      </Alert>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
          variant="destructive"
          onSuccess={() => router.refresh()}
        >
          <Trans>Delete</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
